import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/**
 * Service catalog data layer. Reads are public (booking funnel reads the same
 * tables); writes are plain RLS-granted insert/update to admin + superadmin
 * (0004 services_admin/variants_admin/addons_admin) — catalog edits carry no
 * cross-table business rule, so no Edge Function is required (API
 * Conventions § route types). promo_price is the ACTIVE price every money
 * computation uses — original_price is display-only strikethrough.
 *
 * No DELETE anywhere: bookings reference service_variants forever, so
 * removing a service or a duration switches it off (`services.is_active`,
 * `service_variants.active`) and keeps the row. Remove, Restore, Popular and
 * Featured are Super Admin switches — 0331 refuses them from anyone else in the
 * database, and audits every flip.
 */

export function useCatalog() {
  return useQuery({
    queryKey: ["catalog"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select(
          `id, category, name, blurb, image_path, created_at,
           is_active, is_popular, popular_rank, is_featured, featured_rank,
           service_variants ( id, duration_minutes, original_price, promo_price, active )`,
        )
        .order("category")
        .order("name");
      if (error) throw error;
      return (data ?? []).map((s) => ({
        ...s,
        // 0331: removed durations stay listed, marked, so they can be restored.
        // Until then a removed duration (0281) was filtered out here and could
        // never come back.
        service_variants: (s.service_variants ?? []).sort(
          (a, b) => a.duration_minutes - b.duration_minutes,
        ),
      }));
    },
    staleTime: 5 * 60_000,
  });
}

export type CatalogService = NonNullable<ReturnType<typeof useCatalog>["data"]>[number];

function useInvalidateCatalog() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["catalog"] });
  };
}

export interface ServiceSwitches {
  is_active?: boolean;
  is_popular?: boolean;
  popular_rank?: number | null;
  is_featured?: boolean;
  featured_rank?: number | null;
}

/** Remove/Restore and Popular/Featured on one service (0331, Super Admin only). */
export function useSetServiceSwitches() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: async ({ id, ...patch }: ServiceSwitches & { id: string }) => {
      const { error } = await supabase.from("services").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

/** Remove/Restore one duration (0281's flag; 0331 makes it Super Admin only). */
export function useSetVariantActive() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("service_variants").update({ active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export interface ServiceInput {
  category: string;
  name: string;
  blurb: string | null;
}

export function useCreateService() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: async (input: ServiceInput) => {
      const { data, error } = await supabase.from("services").insert(input).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateService() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: async ({ id, ...patch }: ServiceInput & { id: string }) => {
      const { error } = await supabase.from("services").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

/** The public bucket 0216 created. A photo is marketing shown signed out. */
export const SERVICE_PHOTOS_BUCKET = "service-photos";
const PHOTO_TYPES: readonly string[] = ["image/jpeg", "image/png", "image/webp"];
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

/** The public URL for a stored object path, or null when the service has no photo. */
export function servicePhotoUrl(path: string | null): string | null {
  return path
    ? supabase.storage.from(SERVICE_PHOTOS_BUCKET).getPublicUrl(path).data.publicUrl
    : null;
}

/** Why a picked file cannot be used, or null when it can. */
export function servicePhotoProblem(file: File): string | null {
  if (!PHOTO_TYPES.includes(file.type)) return "Use a JPG, PNG or WebP image.";
  if (file.size > PHOTO_MAX_BYTES) return "That image is over 5 MB.";
  return null;
}

/**
 * Upload a photo and point the service at it.
 *
 * ⚠ A NEW OBJECT PER UPLOAD, NEVER AN OVERWRITE. The bucket is public and served
 * through a CDN, so replacing the bytes at one path would keep showing the old
 * photo to anyone with it cached. A fresh path per upload changes the URL, and
 * the URL is what every client sees.
 *
 * The upload happens first and the row is updated second: a failed upload
 * leaves the service untouched, where the reverse order could point a live
 * service at an object that does not exist.
 */
export function useUploadServicePhoto() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: async ({ serviceId, file }: { serviceId: string; file: File }) => {
      const problem = servicePhotoProblem(file);
      if (problem) throw new Error(problem);
      const ext = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
      const path = serviceId + "/" + Date.now() + "." + ext;
      const { error: uploadError } = await supabase.storage
        .from(SERVICE_PHOTOS_BUCKET)
        .upload(path, file, { contentType: file.type, upsert: false });
      if (uploadError) throw uploadError;
      const { error } = await supabase
        .from("services")
        .update({ image_path: path })
        .eq("id", serviceId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export interface VariantInput {
  service_id: string;
  duration_minutes: number;
  original_price: number;
  promo_price: number;
}

export function useCreateVariant() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: async (input: VariantInput) => {
      const { error } = await supabase.from("service_variants").insert(input);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

/**
 * Price changes take effect for NEW bookings only — existing bookings store
 * gross_amount/commission_base at creation, so editing promo_price never
 * rewrites money on a placed booking (money-math.md).
 */
export function useUpdateVariant() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: async ({
      id,
      ...patch
    }: {
      id: string;
      duration_minutes: number;
      original_price: number;
      promo_price: number;
    }) => {
      const { error } = await supabase.from("service_variants").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}
